<style lang="scss" scoped>
    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
        transition: opacity 1s;
    }
</style>

<template>
    <div>
        <button class='btn btn-warning' v-if="devMode" @click="devMode=false">Disable Dev Debug</button>

        <template v-if="showParticipantCountConfirm && isKnowledgeQuizComplete">
            <div class="form-row d-flex justify-content-center">
                <div class="col-6 alert alert-warning">
                    <p>Do any participants need training credit hours for this training (e.g., professional development registry, training certificate)?</p>
                    <p>This will be the only opportunity for training participants to receive credit. <strong>If you are unsure, mark yes</strong>.</p>
                </div>
            </div>
            <div class="form-row d-flex justify-content-center">
                <div class="col-3">
                    <button type="button" class="btn btn-outline-primary px-4 py-3 w-100" @click="confirmCertificateRequest(false)">No</button>
                </div>
                <div class="col-3">
                    <button type="submit" class="btn btn-primary text-white px-4 py-3 w-100" @click="confirmCertificateRequest(true)">Yes</button>
                </div>
            </div>
        </template>

        <template v-if="!showParticipantCountConfirm && (isKnowledgeQuizComplete || devMode)">
            <form class="sirs-survey" ref="surveyForm" v-show="!isFormMasked" @submit.prevent="submit" novalidate>

                <template v-if="showSurveySlot">
                    <div class="form-row pb-5">
                        <div class="col">
                            <div id="inputHelp" v-if="!isParticipantCountConfirmed" class="question-text">
                                <p>Please list the following information for all participants who need a training certificate or record of participation to receive training credits in their state. Without this information, participants will <u>not</u> be able to receive credit for this training session from Go NAPSACC now or in the future.</p>
                                <ol>
                                    <li>Enter the first and last name of each person who participated in this training session.</li>
                                    <li>Enter each participant's email address – if a participant does not have an email address, Go NAPSACC will use the child care program's Go NAPSACC username as the email address for this participant.</li>
                                    <li>If applicable, enter each participants's state <u :title="trainingIdInfo">training ID <i class="fa fa-info-circle" aria-hidden="true"></i></u>.</li>
                                </ol>
                                <p><strong>Note</strong>: If this training session is for credit in your state <u :title="trainingRegistryInfo">training registry <i class="fa fa-info-circle" aria-hidden="true"></i></u> you will need to enter each participants’ name, email address and state registry/training ID as they appear in your state's registry system.</p>
                            </div>

                        </div>
                    </div>

                    <div class="form-group pb-3">
                        <div class="question-text">
                            Please enter the program's phone number. This textbox only accepts numbers. Please do not include any punctuation.
                        </div>

                        <div v-show="phoneError.length > 0" class="alert alert-warning">
                            {{ phoneError }}
                        </div>

                        <div>
                             <phone-input v-model.format-phone="programPhone"></phone-input>
                        </div>
                    </div>

                    <div class="form-group pb-3">

                        <!-- INPUT FOR PARTICIPANT COUNT -->
                        <div v-if="isParticipantCountConfirmed" id="participant-count-container" class="form-row pb-3">
                            <div class="col-sm-auto col-form-label question-text">Total number of participants who attended this training session</div>
                            <div class="col-sm-auto"><input type="number" name="participant_count" min="1" v-model="participantCount" required></div>
                        </div>

                        <!-- FORM FOR PARTICIPANT NAMES/EMAILS -->
                        <div v-else class='participant-names-container pb-3'>
                            <div class="form-row">
                                <div class="col-1 question-text"></div>
                                <div class="col-2 question-text">First Name</div>
                                <div class="col-3 question-text">Last Name</div>
                                <div class="col-3 question-text">Email</div>
                                <div class="col-3 question-text">Registry ID</div>
                            </div>

                            <div class="form-group mb-3">
                                <div v-show="participantError.length > 0">
                                    <div class="alert alert-warning">{{ participantError }}
                                        <button v-if="showAutofillButton" type="button" class="btn btn-md btn-primary" @click="fillEmptyEmailsWithProgramEmail">Autofill Email</button>
                                    </div>
                                </div>

                                <div id="evaluation-participant-forms">
                                    <div v-for="(p,i) in participants" class="evaluation-participant-form form-row pb-3">
                                        <div class="col-1">
                                            <span class="question-text">({{ i+1 }})</span>
                                        </div>
                                        <div class="col-2">
                                            <input type="text" @input="updateInput" class="first-name" v-model="p.first_name" aria-describedby="inputHelp" placeholder="Enter first name">
                                        </div>
                                        <div class="col-3">
                                            <input type="text" @input="updateInput" class="last-name" v-model="p.last_name" aria-describedby="inputHelp" placeholder="Enter last name">
                                        </div>
                                        <div class="col-3">
                                            <input type="email" @input="updateInput" class="email" v-model="p.email" aria-describedby="inputHelp" placeholder="Enter email">
                                        </div>
                                        <div class="col-2">
                                            <input type="text" @input="updateInput" class="registry-id" v-model="p.registry_id" aria-describedby="inputHelp" placeholder="Enter registry ID">
                                        </div>
                                        <div class="col-1">
                                            <button type="button" class='delete-participant-btn btn btn-danger btn-sm ' @click="deleteParticipant(i)">
                                                <i class="fa fa-trash delete-participant-btn-icon" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div class="form-row clearfix pb-3">
                                        <button type="button" @click="addParticipant()" class="float-right btn btn-success btn-md text-white">
                                            <i class="fa fa-plus-square mr-2" aria-hidden="true"></i>Add new participant
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <survey-modal
                    :training-session="trainingSession"
                    survey-type="evaluation"
                >
                    <template v-slot:surveyPageSlot><div v-if="!showSurveySlot"></div></template>
                    <template v-slot:submitMsg>
                        <transition name='fade'>
                            <div v-if="submitMessageShow" class="alert" :class="submitMessageClass" ref="submit-message" role="alert">{{ submitMessage }}</div>
                        </transition>
                    </template>
                    <template v-slot:submitBtn>
                        <div v-if="showSurveySlot">
                            <div v-if="showSubmitConfirm" class="form-row">
                                <div class="col-6 alert alert-warning">
                                    {{ submitConfirmMessage }}
                                </div>
                                <div class="col-4">
                                    <button type="button" class="btn btn-outline-primary" @click="onClickSubmitConfirmNo">
                                        <i class="fa fa-undo pr-1 align-middle" aria-hidden="true"></i>Go Back
                                    </button>
                                    <button type="submit" class="btn btn-primary text-white" @click="isSubmitConfirmed=true">
                                        <i class="fa fa-arrow-right float-right pl-1 pt-1 align-middle" aria-hidden="true"></i>Submit
                                    </button>
                                </div>
                            </div>
                            <div v-else class="form-row">
                                <div class="col-2">
                                    <button id="nav-finalize" type="button" name="nav" value="finalize" class="btn btn-orange-blue" @click="clickSubmitBtn">Submit</button>
                                </div>
                            </div>
                        </div>
                        <div v-else></div>
                    </template>
                </survey-modal>
            </form>
        </template>

        <template v-if="!isKnowledgeQuizComplete">
            <div class='m-3'>
                You must watch the training video and successfully pass the quiz before completing your evaluation and printing your certificate.
            </div>
        </template>
    </div>
</template>

<script>

    import SurveyModal from './SurveyModal';
    // import VuePhoneNumberInput from 'vue-phone-number-input';
    import { mapState, mapMutations } from 'vuex';
    import PhoneInput from '../PhoneInput.vue';

    export default{
        components: {
            SurveyModal,
            // VuePhoneNumberInput,
            PhoneInput,
        },
        props: {
            trainingSessionId: {
                type: Number,
                default: null
            },
            isDev: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                participants: [{},{},{}],
                participantError: "",
                programPhone: "",
                phoneError: "",
                participantCount: null,
                submitMessageShow: false,
                submitMessage: "Please confirm that all information provided is correct before submitting. Are you ready to submit?",
                submitMessageClass: "alert-warning",
                loading: false,
                devMode: this.isDev,
                showAutofillButton: false,
                showSubmitConfirm: false,
                submitConfirmMessage: "",
                isSubmitConfirmed: false,
                showSurveySlot: true,
                isFormMasked: false,
                showParticipantCountConfirm: true,
            }
        },
        methods: {
            submit() {
                if(this.validate()) {
                    if(this.isSubmitConfirmed) {

                        let vm = this;
                        this.loading = true;
                        let data = new FormData(this.$refs.surveyForm);
                        data.append('program_phone', this.nationalNumber);

                        axios.post('training-session/' + vm.trainingSessionId + '/evaluation', data)
                        .then(response => {
                            let latestEvaluationResponse = response.data.latestEvaluationResponse;
                            let trainingSessionId = response.data.trainingSessionId;
                            let trainingSession = response.data.trainingSession;

                            vm.updateLatestResponse({latestEvaluationResponse, trainingSessionId});
                            vm.updateTrainingSession({trainingSession, trainingSessionId});

                            vm.loading = false;
                            vm.submitMessageClass = "alert-success";
                            vm.showSurveySlot = false;
                            vm.submitMessage = "Thank you for your response! Please return to the trainings page and click on your session name under Completed Training Sessions to print your certificate of training completion.";
                        });

                        let participantData = {
                            participants: this.participants,
                        };

                        axios.post('training-session/' + vm.trainingSessionId + '/evaluation/participants', participantData)
                        .then(response => {
                            let sessionParticipants = response.data.participants;
                            let trainingSessionId = vm.trainingSessionId;

                            vm.updateSessionParticipants({sessionParticipants, trainingSessionId});
                        });

                        this.submitMessageShow = true;
                    } else {
                        this.showSubmitConfirm = true;
                        if(this.participants.length == 0) {
                            this.submitConfirmMessage = "You have not entered any participant names, meaning no certificates will be available for participants. Are you ready to submit?";
                        } else {
                            this.submitConfirmMessage = "Please confirm that all information is provided is correct before submitting. Are you ready to submit?";
                        }
                    }

                } else {
                    this.showSubmitAlert();
                }
            },
            clickSubmitBtn() {
                if(this.validate()) {
                    this.showSubmitConfirm = true;
                    if(this.participants.length == 0) {
                        this.submitConfirmMessage = "You have not entered any participant names, meaning no certificates will be available for participants. Are you ready to submit?";
                    } else {
                        this.submitConfirmMessage = "Please confirm that all information is provided is correct before submitting. Are you ready to submit?";
                    }
                } else {
                    this.showSubmitAlert();
                }
            },
            showSubmitAlert() {
                this.submitMessage = "Please scroll up for error messages.";
                this.submitMessageClass = "alert-danger";
                this.submitMessageShow = true;
                setTimeout(() => {
                    this.submitMessageShow = false;
                }, 3000);
            },
            validate() {
                this.participantError = "";
                this.phoneError = "";
                this.submitMessage = "";
                this.showAutofillButton = false;
                this.submitMessageShow = false;
                this.showSubmitConfirm = false;

                // exclude empty participants from validation
                this.participants = this.participants.filter( p => {
                    return p.first_name || p.last_name || p.first_name || p.registry_id;
                });

                // get participants that are missing name(s)
                let partsWithoutName = this.participants.filter(p => {
                    return !p.first_name || !p.last_name
                });

                // get participants that are missing email
                let partsWithoutEmail = this.participants.filter(p => {
                    return !p.email;
                });

                // check if at least one participant
                if(this.participants.length == 0 && !this.isParticipantCountConfirmed) {
                    this.participantError = "Please include at least one participant name.";
                    // make sure there is at least one participant
                    this.participants.push({});
                    return false;
                }

                // check for participants without name
                if(partsWithoutName.length > 0) {
                    this.participantError = "Please include first and last name for each participant."
                    return false;
                }

                // check for participants without email
                if(partsWithoutEmail.length > 0) {
                    this.showAutofillButton = true;
                    this.participantError = "Individual email addresses are required to link to some state training registries. Please either fill in with individual email addresses or click the AUTOFILL EMAIL button to autofill with the program's user name.";
                    return false;
                }

                // check for phone number
                let phoneRegex = new RegExp(/^\(\d{3}\)\s\d{3}-\d{4}/);
                if(!phoneRegex.test(this.programPhone)) {
                    this.phoneError = "Please include a valid phone number, e.g. (123) 456-7890";
                    return false;
                }

                // do survey form validation
                if(!this.$refs.surveyForm.checkValidity()) {
                    this.$refs.surveyForm.reportValidity();
                    return false;
                }

                return true;
            },
            fillEmptyEmailsWithProgramEmail() {
                this.participantError = "";

                this.participants.forEach((p) => {
                    if(!p.email) {
                        p.email = this.trainingSession.program.users[0].email;
                    }
                });
            },
            addParticipant: function() {
                let emptyParticipants = [{}];
                this.participants.push(...emptyParticipants);
            },
            deleteParticipant: function(index) {
                this.participants.splice(index, 1);
            },
            updateInput: function(event) {
                this.$emit('update:child-participants', this.participants);
            },
            ...mapMutations({
                updateLatestResponse: 'UPDATE_LATEST_EVALUATION_RESPONSE',
                updateTrainingSession: 'UPDATE_TRAINING_SESSION',
                updateSessionParticipants: 'UPDATE_SESSION_PARTICIPANTS',
            }),
            confirmCertificateRequest(needsCertificate) {
                this.isParticipantCountConfirmed = !needsCertificate;
                this.showParticipantCountConfirm = false;
            },
            onClickSubmitConfirmNo() {
                this.showSubmitConfirm = !this.showSubmitConfirm;
                this.showParticipantCountConfirm = true;
                this.participants = [{},{},{}];
                this.participantCount = null;
            }
        },
        computed: {
            trainingSession() {
                return this.trainingSessions[this.trainingSessionId];
            },
            submitTemplate() {
                if(this.loading) {
                    return "<i class='fa fa-spin fa-spinner' aria-hidden='true'></i>";
                }
                return "Submit";
            },
            isKnowledgeQuizComplete() {
                let response = this.latestQuizResponses[this.trainingSessionId];
                if(response) {
                    return response.status === 'Completed';
                }
                return false;
            },
            trainingRegistryInfo() {
                return "A list of training courses that meet state training requirements for continued education credits and/or contact hours.";
            },
            trainingIdInfo() {
                return "An ID number (registry ID, Professional Development ID) that links you to your state training registry. Not required in some states.";
            },
            nationalNumber() {
                return this.programPhone;
            },
            ...mapState([
                'trainingSessions',
                'sessionParticipants',
                'latestQuizResponses',
                'currentTrainingSession'
            ])
        },
    }
</script>
